import { SortingState } from '@tanstack/react-table';
import { del, get, set } from 'idb-keyval';
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

const idbStorage = {
  getItem: async (name: string) => await get(name),
  setItem: async (name: string, value: any) => {
    return await set(name, value);
  },
  removeItem: async (name: string) => await del(name),
};

interface Pagination {
  pageIndex: number;
  pageSize: number;
}

export interface TableState {
  table: string;
  pagination: Pagination;
  filters: Record<string, any>;
  columnsVisible: Record<string, boolean>;
  sorting: SortingState;
  selectedRows: {
    rowId: string;
    value: any;
  }[];
}

export interface TablesState {
  rehydrated: boolean;
  setHasHydrated: (hydrated: boolean) => void;
  tables: TableState[];
  initTable: (
    table: string,
    filters: Record<string, any>,
    sorting: SortingState,
    columns: Record<string, boolean>,
  ) => void;
  setTablePagination: (table: string, pagination: Pagination) => void;
  setTableSorting: (table: string, sorting: SortingState) => void;
  setTableFilters: (table: string, filters: Record<string, any>) => void;
  setColumnsVisible: (table: string, columns: Record<string, boolean>) => void;
  setSelectedRows: (table: string, rows: any[]) => void;
  removeSelectedRow: (table: string, rowId: string) => void;
  removeTableState: (table: string) => void;
  clearAll: () => void;
}

const useTableStore = create<TablesState>()(
  devtools(
    persist(
      (set) => ({
        rehydrated: false,
        setHasHydrated: (hydrated: boolean) => {
          set({
            rehydrated: hydrated,
          });
        },
        tables: [],
        initTable: (
          table: string,
          filters: Record<string, any>,
          sorting: SortingState,
          columnsVisible: Record<string, boolean>,
        ) =>
          set((state: TablesState) => {
            const tables = [...state.tables];
            const tableIndex = tables.findIndex((t) => t.table === table);

            if (tableIndex === -1) {
              tables.push({
                table,
                sorting,
                filters,
                columnsVisible,
                pagination: {
                  pageIndex: 0,
                  pageSize: 10,
                },
                selectedRows: [],
              });
            }

            return { tables };
          }),
        setTablePagination: (table: string, pagination: Pagination) =>
          set((state: TablesState) => {
            const tables = [...state.tables];
            const tableIndex = tables.findIndex((t) => t.table === table);

            if (tableIndex !== -1) {
              tables[tableIndex].pagination = pagination;
            }

            return { tables };
          }),
        setTableSorting: (table: string, sorting: SortingState) =>
          set((state: TablesState) => {
            const tables = [...state.tables];
            const tableIndex = tables.findIndex((t) => t.table === table);

            if (tableIndex !== -1) {
              tables[tableIndex].sorting = sorting;
            }

            return { tables };
          }),

        setTableFilters: (table: string, filters: Record<string, any>) =>
          set((state: TablesState) => {
            const tables = [...state.tables];
            const tableIndex = tables.findIndex((t) => t.table === table);

            if (tableIndex !== -1) {
              tables[tableIndex].filters = { ...tables[tableIndex].filters, ...filters };
            }

            return { tables };
          }),
        setColumnsVisible: (table: string, columns: Record<string, boolean>) =>
          set((state: TablesState) => {
            const tables = [...state.tables];
            const tableIndex = tables.findIndex((t) => t.table === table);

            if (tableIndex !== -1) {
              tables[tableIndex].columnsVisible = columns;
            }

            return { tables };
          }),

        setSelectedRows: (table: string, rows: any[]) =>
          set((state: TablesState) => {
            const tables = [...state.tables];
            const tableIndex = tables.findIndex((t) => t.table === table);

            if (tableIndex !== -1) {
              tables[tableIndex].selectedRows = rows;
            }

            return { tables };
          }),

        removeSelectedRow: (table: string, rowId: string) =>
          set((state: TablesState) => {
            const tables = [...state.tables];
            const tableIndex = tables.findIndex((t) => t.table === table);

            if (tableIndex !== -1) {
              tables[tableIndex].selectedRows = tables[tableIndex].selectedRows.filter(
                (row) => row.rowId !== rowId,
              );
            }

            return { tables };
          }),

        removeTableState: (table: string) =>
          set((state: TablesState) => ({ tables: state.tables.filter((t) => t.table !== table) })),

        clearAll: () => set(() => ({ tables: [] })),
      }),
      {
        name: 'table-storage',
        storage: idbStorage,
        partialize: (state) => ({
          tables: state.tables,
          rehydrated: state.rehydrated,
        }),
        onRehydrateStorage: (state) => {
          return () => state.setHasHydrated(true);
        },
      },
    ),
  ),
);

export default useTableStore;
